import ko from 'knockout';
import moment from 'moment';
import { CustomError, customFetch as fetch } from '../../fetch';
import { errorToast, successToast } from '../../toastr-helper';
import { closePopups, openPopup } from '../../init';
import { formatDecimal } from '../../decimal-utils';
import { Listview } from '../../listview';

export enum EmployeeSickLeaveStatus {
    ReportedSick,
    ReportedBetter,
    'Beter gemeld' = ReportedBetter,
    'Ziek gemeld' = ReportedSick
}

export interface EmployeeSickLeaveModelOptions {
    employeeId: string;

    editPopupId: string;

    // GET url: all sick leaves of employee
    allSickLeavesUrl: string;

    // GET url: overview of all sick leaves of employee
    overviewSickLeavesUrl: string;

    // GET url: all sick leave documents for particular sick leave
    allSickLeaveDocumentsUrl: string;

    // GET url: all sick leave history lines for particular sick leave
    allSickLeaveHistoryUrl: string;

    // POST url: report employee better
    reportBetterUrl: string;

    // POST url: add sick leave for employee
    addSickLeaveUrl: string;

    // POST url: edit sick leave for employee
    editEmployeeSickLeaveUrl: string;

    // DELETE url: delete sick leave document for sick leave
    deleteEmployeeSickLeaveDocumentUrl: string;

    // Listview ref containing all sick leaves
    listviewRef: Listview;

    // Listview ref contaning all documents for currently opened (in edit popup) sick leave
    sickLeaveDocumentsListviewRef: Listview;

    // Listivew ref containing all history lines for currently opened (in edit popup) sick leave
    sickLeaveHistoryListviewRef: Listview;

    // Boolean flag to indicate whether the sick leave modes are rendered in the employer area
    isFromEmployer: boolean;
     
    settings: EmployeeSickLeaveSettings;
}

export default class EmployeeSickLeaveModel {
    addEmployeeSickLeaveModel: KnockoutObservable<AddEmployeeSickLeaveModel>;
    reportBetterModel: KnockoutObservable<ReportBetterModel>;
    overviewModel: KnockoutObservable<EmployeeSickLeaveOverviewModel>;
    editEmployeeSickLeaveModel: KnockoutObservable<EditEmployeeSickLeaveModel>;
    deleteEmployeeSickLeaveDocumentModel: KnockoutObservable<DeleteEmployeeSickLeaveDocumentModel>;
    saving: KnockoutObservable<boolean>;

    options: EmployeeSickLeaveModelOptions;

    constructor(options: EmployeeSickLeaveModelOptions) {
        this.saving = ko.observable<boolean>(false);
        this.overviewModel = ko.observable<EmployeeSickLeaveOverviewModel>(new EmployeeSickLeaveOverviewModel(options));
        this.reportBetterModel = ko.observable<ReportBetterModel>(new ReportBetterModel(options, this.overviewModel));
        this.addEmployeeSickLeaveModel = ko.observable<AddEmployeeSickLeaveModel>(new AddEmployeeSickLeaveModel(options, this.overviewModel));
        this.editEmployeeSickLeaveModel = ko.observable<EditEmployeeSickLeaveModel>(new EditEmployeeSickLeaveModel(options, this.overviewModel));
        this.deleteEmployeeSickLeaveDocumentModel = ko.observable<DeleteEmployeeSickLeaveDocumentModel>(new DeleteEmployeeSickLeaveDocumentModel(options.deleteEmployeeSickLeaveDocumentUrl, options.editPopupId, options.sickLeaveDocumentsListviewRef, options.sickLeaveHistoryListviewRef));
        this.options = options;
    }

    resetAddSickLeave() {
        this.addEmployeeSickLeaveModel().reset();
    }
}

export class EmployeeSickLeaveSettings {
    hasPlusMinModule: boolean;
    hasSignRequestModule: boolean;
    
    absenteeismEnabled: boolean;
    offDaysEnabled: boolean;
    
    hourBalanceExclusionDefault: boolean;
}

class DeleteEmployeeSickLeaveDocumentModel {
    employeeSickLeaveDocumentId: KnockoutObservable<string>;
    saving: KnockoutObservable<boolean>;

    editPopupId: string;
    deleteEmployeeSickLeaveDocumentUrl: string;
    sickLeaveDocumentsListviewRef: Listview;
    sickLeaveHistoryListviewRef: Listview;

    constructor(deleteEmployeeSickLeaveDocumentUrl: string, editPopupId: string, sickLeaveDocumentsListviewRef: Listview, sickLeaveHistoryListviewRef: Listview) {
        this.employeeSickLeaveDocumentId = ko.observable<string>();
        this.saving = ko.observable<boolean>();

        this.deleteEmployeeSickLeaveDocumentUrl = deleteEmployeeSickLeaveDocumentUrl;
        this.editPopupId = editPopupId;
        this.sickLeaveDocumentsListviewRef = sickLeaveDocumentsListviewRef;
        this.sickLeaveHistoryListviewRef = sickLeaveHistoryListviewRef;
    }

    closePopup() {
        closePopups();
    }

    async submit() {
        const fd = new FormData();
        fd.append('id', this.employeeSickLeaveDocumentId());

        this.saving(true);

        const response = await fetch(this.deleteEmployeeSickLeaveDocumentUrl, {
            method: 'DELETE',
            body: fd
        });

        this.saving(false);

        if (response.ok) {
            successToast('Het document is succesvol verwijderd');
            closePopups();
            openPopup(this.editPopupId);
            this.sickLeaveDocumentsListviewRef.load();
            this.sickLeaveHistoryListviewRef.load();
        } else {
            errorToast('Er ging iets mis tijdens het verwijderen van het document');
        }
    }
}

class EmployeeSickLeaveOverviewModel {
    validationErrors: KnockoutObservableArray<CustomError>;
    totalSickDays: KnockoutObservable<number>;
    totalOffDays: KnockoutObservable<number>;
    
    overviewUrl: string;
    employeeId: string;

    settings: EmployeeSickLeaveSettings;
    hasSignRequestModule: boolean;

    constructor(options: EmployeeSickLeaveModelOptions) {
        this.validationErrors = ko.observableArray<CustomError>();
        this.totalSickDays = ko.observable<number>(0);
        this.totalOffDays = ko.observable<number>(0);

        this.overviewUrl = options.overviewSickLeavesUrl;
        this.employeeId = options.employeeId;
        this.settings = options.settings;
        this.hasSignRequestModule = options.settings.hasSignRequestModule;

        this.refresh();
    }

    async refresh() {
        const response = await fetch(this.overviewUrl);

        if (response.ok) {
            let data = response.data[0];

            this.totalSickDays(data.totalSickDays);
            this.totalOffDays(data.totalOffDays);
        }
    }
}

class ReportBetterModel {
    employeeSickLeaveId: KnockoutObservable<string>;
    validationErrors: KnockoutObservableArray<CustomError>;
    saving: KnockoutObservable<boolean>;
    settings: EmployeeSickLeaveSettings;

    minDate: KnockoutObservable<moment.Moment>;
    betterDate: KnockoutObservable<moment.Moment>;
    sickDays: KnockoutObservable<number>;
    addOffDays: KnockoutObservable<boolean>;
    hourBalanceExclusion: KnockoutObservable<boolean>;

    overviewModel: KnockoutObservable<EmployeeSickLeaveOverviewModel>;
    reportBetterUrl: string;
    listviewRef: Listview;

    constructor(options: EmployeeSickLeaveModelOptions, overviewModel: KnockoutObservable<EmployeeSickLeaveOverviewModel>) {
        this.employeeSickLeaveId = ko.observable<string>();
        this.validationErrors = ko.observableArray<CustomError>();
        this.saving = ko.observable<boolean>();
        this.settings = options.settings;

        this.minDate = ko.observable<moment.Moment>(moment());        
        this.betterDate = ko.observable<moment.Moment>(moment())
            .extend({
                errorHandler: { source: this.validationErrors, id: 'BetterDate' }
            });
        this.sickDays = ko.observable<number>()
            .extend({
                errorHandler: { source: this.validationErrors, id: 'SickDays' }
            });
        this.addOffDays = ko.observable<boolean>(true);
        this.hourBalanceExclusion = ko.observable<boolean>(this.settings.hourBalanceExclusionDefault);

        this.overviewModel = overviewModel;
        this.reportBetterUrl = options.reportBetterUrl;
        this.listviewRef = options.listviewRef;
    }

    closePopup() {
        closePopups();
    }

    async submit() {
        const fd = new FormData();
        fd.append('id', this.employeeSickLeaveId());
        fd.append('betterDate', this.betterDate().format('YYYY-MM-DD'));
        fd.append('addOffDays', this.addOffDays().toString());
        fd.append('hourBalanceExclusion', this.hourBalanceExclusion().toString());
        
        const sickDays = this.sickDays()
            ? formatDecimal(this.sickDays())
            : '0';
        fd.append('sickDays', sickDays);

        this.saving(true);

        const response = await fetch(this.reportBetterUrl, {
            method: 'POST',
            body: fd
        });

        this.saving(false);

        if (response.ok) {
            successToast('De wijzigingen zijn opgeslagen');
            this.listviewRef.load();
            this.overviewModel().refresh();
            closePopups();
        } else if (response.errors && response.errors.length > 0) {
            this.validationErrors(response.errors);
            errorToast('Er ging iets mis bij het opslaan');
        } else if (response.data.length > 0) {
            errorToast(response.data);
        }
    }
}

class EditEmployeeSickLeaveModel {
    employeeSickLeaveId: KnockoutObservable<string>;
    saving: KnockoutObservable<boolean>;
    validationErrors: KnockoutObservableArray<CustomError>;
    settings: EmployeeSickLeaveSettings;
    hasSignRequestModule: boolean;

    minDate: KnockoutObservable<moment.Moment>;
    fromDate: KnockoutObservable<moment.Moment>;
    actualEndDate: KnockoutObservable<moment.Moment>;
    prognosedEndDate: KnockoutObservable<moment.Moment>;
    mutationDate: KnockoutObservable<moment.Moment>;
    sickPercentage: KnockoutObservable<number>;
    currentSickPercentage: KnockoutObservable<number>;
    sickDays: KnockoutObservable<number>;
    prolonged: KnockoutObservable<boolean>;
    dueToPregnancy: KnockoutObservable<boolean>;
    hourBalanceExclusion: KnockoutObservable<boolean>;
    isReportedBetter: KnockoutObservable<boolean>;

    file: KnockoutObservable<any>;
    requiresEmployeeSignature: KnockoutObservable<boolean>;
    requiresEmployerSignature: KnockoutObservable<boolean>;

    targetListview: Listview;
    sickLeaveDocumentsListviewRef: Listview;
    sickLeaveHistoryListviewRef: Listview;
    editEmployeeSickLeaveUrl: string;
    allSickLeaveDocumentsUrl: string;
    allSickLeaveHistoryUrl: string;
    employeeId: string;
    isFromEmployer: boolean;
    overviewModel: KnockoutObservable<EmployeeSickLeaveOverviewModel>;

    constructor(options: EmployeeSickLeaveModelOptions, overviewModel: KnockoutObservable<EmployeeSickLeaveOverviewModel>) {
        this.employeeSickLeaveId = ko.observable<string>();
        this.saving = ko.observable<boolean>();
        this.validationErrors = ko.observableArray<CustomError>();
        this.settings = options.settings;

        this.minDate = ko.observable<moment.Moment>(moment().startOf('d'));
        this.fromDate = ko.observable<moment.Moment>()
            .extend({
                errorHandler: { source: this.validationErrors, id: 'FromDate' }
            });
        this.actualEndDate = ko.observable<moment.Moment>()
            .extend({
                errorHandler: { source: this.validationErrors, id: 'ToDate' }
            });
        this.prognosedEndDate = ko.observable<moment.Moment>()
            .extend({
                errorHandler: { source: this.validationErrors, id: 'PrognosedEndDate' }
            });
        this.mutationDate = ko.observable<moment.Moment>()
            .extend({
                errorHandler: { source: this.validationErrors, id: 'MutationDate' }
            });
        this.sickPercentage = ko.observable<number>(100)
            .extend({
                errorHandler: { source: this.validationErrors, id: 'SickPercentage' }
            });
        this.currentSickPercentage = ko.observable<number>(100)
            .extend({
                errorHandler: { source: this.validationErrors, id: 'SickPercentage' }
            });
        this.sickDays = ko.observable<number>()
            .extend({
                errorHandler: { source: this.validationErrors, id: 'SickDays' }
            });

        this.prolonged = ko.observable<boolean>();
        this.dueToPregnancy = ko.observable<boolean>();;
        this.isReportedBetter = ko.observable<boolean>();
        this.hourBalanceExclusion = ko.observable<boolean>(this.settings.hourBalanceExclusionDefault);

        this.file = ko.observable<any>();
        this.requiresEmployeeSignature = ko.observable<boolean>(this.settings.hasSignRequestModule);
        this.requiresEmployerSignature = ko.observable<boolean>();

        this.overviewModel = overviewModel;
        this.targetListview = options.listviewRef;
        this.sickLeaveDocumentsListviewRef = options.sickLeaveDocumentsListviewRef;
        this.sickLeaveHistoryListviewRef = options.sickLeaveHistoryListviewRef;
        this.editEmployeeSickLeaveUrl = options.editEmployeeSickLeaveUrl;
        this.allSickLeaveDocumentsUrl = options.allSickLeaveDocumentsUrl;
        this.allSickLeaveHistoryUrl = options.allSickLeaveHistoryUrl;
        this.employeeId = options.employeeId;
        this.isFromEmployer = options.isFromEmployer;
        this.hasSignRequestModule = options.settings.hasSignRequestModule;
    }

    init(model: any = {}) {
        this.validationErrors([]);
        this.employeeSickLeaveId(model.id);
        this.fromDate(moment(model.startDate));
        this.actualEndDate(moment(model.actualEndDate));
        this.prognosedEndDate(moment(model.prognosedEndDate));
        this.sickPercentage(model.sickPercentage);
        this.currentSickPercentage(model.sickPercentage);
        this.sickDays(model.sickDays);
        this.prolonged(model.prolonged);
        this.dueToPregnancy(model.dueToPregnancy);
        this.isReportedBetter(model.status === EmployeeSickLeaveStatus[EmployeeSickLeaveStatus.ReportedBetter]);
        this.hourBalanceExclusion(model.hourBalanceExclusion);

        this.sickLeaveDocumentsListviewRef.options.getItems = async () => {
            const response = await fetch(`${this.allSickLeaveDocumentsUrl}/${model.id}`);

            if (response.ok) {
                return response.data;
            }

            return [];
        };

        this.sickLeaveHistoryListviewRef.options.getItems = async () => {
            const response = await fetch(`${this.allSickLeaveHistoryUrl}/${model.id}`);

            if (response.ok) {
                return response.data;
            }

            return [];
        };

        this.sickLeaveDocumentsListviewRef.load();
        this.sickLeaveHistoryListviewRef.load();
    }

    closePopup() {
        closePopups();
    }

    openPopup() {
        this.mutationDate(moment());
        openPopup('add-sickPercentage-mutation');
    }

    async save() {
        if (this.sickPercentage() !== this.currentSickPercentage()) {
            this.openPopup();
        } else {
            this.submit();
        }
    }

    async submit() {
        const fd = new FormData();
        fd.append('employeeSickLeaveId', this.employeeSickLeaveId());

        if (this.fromDate() && this.fromDate().isValid()) {
            fd.append('fromDate', this.fromDate().format('YYYY-MM-DD'));
        }

        if (this.actualEndDate() && this.actualEndDate().isValid()) {
            fd.append('actualEndDate', this.actualEndDate().format('YYYY-MM-DD'));
        }

        const sickPercentage = this.sickPercentage()
            ? formatDecimal(this.sickPercentage())
            : '';

        const sickDays = this.sickDays()
            ? formatDecimal(this.sickDays())
            : '';

        fd.append('sickPercentage', sickPercentage);
        fd.append('sickDays', sickDays);
        fd.append('prolonged', this.prolonged().toString());
        fd.append('dueToPregnancy', this.dueToPregnancy().toString());
        fd.append('hourBalanceExclusion', this.hourBalanceExclusion().toString());

        if (this.prognosedEndDate()) {
            fd.append('prognosedEndDate', this.prognosedEndDate().format('YYYY-MM-DD'));
        }

        if (this.mutationDate()) {
            fd.append('mutationDate', this.mutationDate().format('YYYY-MM-DD'));
        }

        if (this.file() && this.file().file()) {
            fd.append('file', this.file().file());

            if (this.isFromEmployer) {
                if (this.requiresEmployeeSignature()) {
                    fd.append('requiresEmployeeSignature', this.requiresEmployeeSignature().toString());
                }

                if (this.requiresEmployerSignature()) {
                    fd.append('requiresEmployerSignature', this.requiresEmployerSignature().toString());
                }
            }
        }

        if (!!this.employeeId) {
            fd.append('employeeId', this.employeeId);
        }

        this.saving(true);

        const response = await fetch(this.editEmployeeSickLeaveUrl, {
            method: 'POST',
            body: fd
        });

        this.saving(false);

        if (response.ok) {
            this.targetListview.load();
            this.overviewModel().refresh();
            successToast('De wijzigingen zijn opgeslagen');

            this.file().clear();
            this.requiresEmployeeSignature(true);
            this.requiresEmployerSignature(false);
            this.validationErrors([]);
            this.sickLeaveDocumentsListviewRef.load();
            this.sickLeaveHistoryListviewRef.load();
            this.closePopup();
        } else if (response.errors && response.errors.length > 0) {
            this.validationErrors(response.errors);
            errorToast('Er ging iets mis bij het opslaan');
        }
    }
}

class AddEmployeeSickLeaveModel {
    employeeId: KnockoutObservable<string>;
    saving: KnockoutObservable<boolean>;
    validationErrors: KnockoutObservableArray<CustomError>;
    settings: EmployeeSickLeaveSettings;
    
    minDate: KnockoutObservable<moment.Moment>;
    fromDate: KnockoutObservable<moment.Moment>;
    prognosedEndDate: KnockoutObservable<moment.Moment>;
    sickPercentage: KnockoutObservable<number>;
    overviewModel: KnockoutObservable<EmployeeSickLeaveOverviewModel>;
    prolonged: KnockoutObservable<boolean>;
    dueToPregnancy: KnockoutObservable<boolean>;
    hourBalanceExclusion: KnockoutObservable<boolean>;

    targetListview: Listview;
    addSickLeaveUrl: string;
    isFromEmployer: boolean;
    file: KnockoutObservable<any>;
    requiresEmployeeSignature: KnockoutObservable<boolean>;
    requiresEmployerSignature: KnockoutObservable<boolean>;

    hasSignRequestModule: boolean;

    constructor(options: EmployeeSickLeaveModelOptions, overviewModel: KnockoutObservable<EmployeeSickLeaveOverviewModel>) {
        this.validationErrors = ko.observableArray<CustomError>();
        this.employeeId = ko.observable<string>(options.employeeId);
        this.saving = ko.observable<boolean>(false);
        this.settings = options.settings;
        
        this.minDate = ko.observable<moment.Moment>(moment().startOf('d'));

        this.fromDate = ko.observable<moment.Moment>(moment())
            .extend({
                errorHandler: { source: this.validationErrors, id: 'FromDate' }
            });
        this.prognosedEndDate = ko.observable<moment.Moment>()
            .extend({
                errorHandler: { source: this.validationErrors, id: 'PrognosedEndDate' }
            });
        this.sickPercentage = ko.observable<number>(100)
            .extend({
                errorHandler: { source: this.validationErrors, id: 'SickPercentage' }
            });
        this.prolonged = ko.observable<boolean>(false);
        this.dueToPregnancy = ko.observable<boolean>(false);
        this.hourBalanceExclusion = ko.observable<boolean>(this.settings.hourBalanceExclusionDefault);

        this.file = ko.observable<any>();
        this.requiresEmployeeSignature = ko.observable<boolean>(false);
        this.requiresEmployerSignature = ko.observable<boolean>(false);

        this.hasSignRequestModule = this.settings.hasSignRequestModule;

        this.overviewModel = overviewModel;

        this.targetListview = options.listviewRef;
        this.addSickLeaveUrl = options.addSickLeaveUrl;
        this.isFromEmployer = options.isFromEmployer;
    }

    closePopup() {
        closePopups();
    }

    reset() {
        this.validationErrors([]);
        this.file().clear();
        this.fromDate(moment());
        this.sickPercentage(100);
        this.prognosedEndDate(null);
        this.validationErrors([]);
        this.saving(false);
        this.requiresEmployeeSignature(false);
        this.requiresEmployerSignature(false);
        this.dueToPregnancy(false);
        this.hourBalanceExclusion(this.settings.hourBalanceExclusionDefault);
        this.prolonged(false);
    }

    async submit() {
        const fd = new FormData();
        fd.append('employeeId', this.employeeId());

        if (this.fromDate()) {
            fd.append('fromDate', this.fromDate().format('YYYY-MM-DD'));
        }

        const sickPercentage = this.sickPercentage()
            ? formatDecimal(this.sickPercentage())
            : '';

        fd.append('sickPercentage', sickPercentage);
        fd.append('prolonged', this.prolonged().toString());
        fd.append('dueToPregnancy', this.dueToPregnancy().toString());
        fd.append('hourBalanceExclusion', this.hourBalanceExclusion().toString());

        if (this.prognosedEndDate()) {
            fd.append('prognosedEndDate', this.prognosedEndDate().format('YYYY-MM-DD'));
        }

        if (this.file() && this.file().file()) {
            fd.append('file', this.file().file());

            if (this.isFromEmployer) {
                fd.append('requiresEmployeeSignature', this.requiresEmployeeSignature().toString());
                fd.append('requiresEmployerSignature', this.requiresEmployerSignature().toString());
            }
        }
        
        this.saving(true);

        const response = await fetch(this.addSickLeaveUrl, {
            method: 'POST',
            body: fd
        });
        this.saving(false);

        if (response.ok) {
            this.targetListview.load();
            this.overviewModel().refresh();
            successToast('De wijzigingen zijn opgeslagen');
            closePopups();
        } else if (response.errors && response.errors.length > 0) {
            this.validationErrors(response.errors);
            errorToast('Er ging iets mis bij het opslaan');
        } else if (response.data.length > 0) {
            errorToast(response.data);
        }        
    }
}

(<any>ko).fileBindings.defaultOptions.noFileText = 'Geen document gekozen';
(<any>ko).fileBindings.defaultOptions.buttonText = 'Kies document';
